// Band-height distribution for the Productivity Chart: every band is toggleable and
// self-collapsing, so heights are computed from what is actually visible rather than hardcoded -
// the same mechanism serves both a user's toggle and Sensor gating, since from this module's
// point of view "no weight Sensor" is just another reason a band isn't visible.

import type { MetricFlags } from './productivityMetrics';

export type BandBox = { top: number; height: number };

export type BandLayout = {
    // The thin Production Group context band. Unaffected by weight-Sensor gating or the signal
    // toggles below - it draws whenever there is at least one Production Group to name.
    productionGroups: BandBox | null;
    // The thin Feed Mix context band, right below the Production Group one. Independent of both -
    // a Feed Mix commonly outlives a Production Group, so this band draws whenever there is at least one
    // Feed Mix block, whether or not there is a Production Group to go with it.
    feedMixes: BandBox | null;
    // Shared by weight and Daily Gain (they overlay the same box on opposite axes) - visible
    // whenever either signal is toggled on.
    weight: BandBox | null;
    // Shared by water actual and Water Norm (they overlay the same box, same axis, norm drawn
    // behind actual) - visible whenever either is toggled on.
    water: BandBox | null;
    // Its own sub-band, read against the weight curve via its marked 100 baseline.
    growthIndex: BandBox | null;
    // Its own band, off by default - see productivity.deadCountBand. Unlike weight/Daily
    // Gain/Growth Index, never gated by hasWeightSensor: dead count comes from pig movements, not
    // a Sensor, so it is available - and toggleable - independently of the weight bands above.
    deadCount: BandBox | null;
};

// The two context bands and the gaps are sized in pixels, not as a share of the plot: what they
// have to hold is fixed - one 20px bar, two rows of marker pins, a caption's worth of clearance -
// and none of it grows when the panel does. Left proportional they scale with the chart, so on a
// tall window they take several hundred pixels to draw the same two bars while the data bands they
// are context for get correspondingly less.
//
// Tall enough to hold the Production Group bar plus the two rows of event marks above it. Was 88px,
// which spent a third of the context band on whitespace between marks that are now a 12px triangle
// and a 21px pin rather than two full-width flags - and that third belongs to the data underneath.
// See the row constants in productivityContextBands.ts for how the 64 is divided - they are
// derived from this, so a change here moves the rows with it.
export const PRODUCTION_GROUP_BAND_HEIGHT_PX = 64;
// The bar is 20px and its name is drawn inside it: given less room than the two together need,
// Highcharts does not shrink or clip the name, it silently parks the label off-canvas and the band
// renders as an unlabelled grey slab.
const FEED_MIX_BAND_HEIGHT_PX = 36;
// Enough to hold a band's own caption, which is drawn above its top-left corner, *and* the hairline
// that divides it from the band above (see drawBandSeparators). The rule sits at the top of the gap
// and the caption below it, so the caption reads as belonging to the band it labels rather than
// floating between two - which needs both to fit without touching.
const BAND_GAP_PX = 24;

// Bands that share one plot box rather than sitting in two, keyed by the band that follows the one
// it fuses with. Weight and water are the pair: they are the two primary reads, and a reader
// answering "was it drinking less while it stopped growing?" has to hold both curves in one glance.
// A gap between them makes that two glances at two charts. Their scales stay separate - kg on the
// left, litres on the right - so nothing is being claimed about a shared unit; only the empty strip
// between them goes.
const FUSED_WITH_BAND_ABOVE: Partial<Record<'weight' | 'water' | 'growthIndex' | 'deadCount', 'weight' | 'water' | 'growthIndex' | 'deadCount'>> = {
    water: 'weight',
};

// The weight+Daily Gain and water+Water Norm bands are primary reads; Growth Index is a secondary
// sub-band skimmed against the weight curve, not a co-equal band - matches the 2:1 height ratio
// already used for the same weight/Growth-Index pair in the weight detail modal's combined chart
// (260px) vs its Growth Index chart (220px, rounded). The dead count is off by default and, when shown,
// reads like Growth Index does: a secondary band skimmed against the primary weight curve above it.
const BAND_HEIGHT_RATIO: Record<'weight' | 'water' | 'growthIndex' | 'deadCount', number> = {
    weight: 2,
    water: 2,
    growthIndex: 1,
    deadCount: 1,
};

// A band whose Sensor exists but which holds no reading anywhere in the selected range still has
// to appear - it is the only place the reader learns that this metric is missing rather than flat -
// but it earns the smallest slice rather than its full ratio. Left at full height it is a void
// several hundred pixels tall that crushes the bands that do have something to show.
const EMPTY_BAND_RATIO = 0.6;

/**
 * Every currently-visible band's {top, height} as percentages of the plot area. A band that is
 * hidden - by its own toggle, by its sibling toggle both being off, or by the Section having no
 * weight/water Sensor - gets `null` here rather than a zero-height box, so callers hide the
 * matching axis outright instead of rendering an empty sliver. Whatever height a hidden band would
 * have taken is redistributed across the bands that remain, so the chart always fills the panel.
 */
export function computeBandLayout(
    toggles: MetricFlags,
    hasProductionGroups: boolean,
    hasWeightSensor: boolean,
    hasWaterSensor: boolean,
    hasFeedMixes: boolean = false,
    // Which of the data bands hold no reading at all across the selected range. Distinct from the
    // visibility flags above: these bands still draw, at EMPTY_BAND_RATIO's reduced height, so the
    // absence itself stays on screen.
    emptyBands: Partial<Record<'weight' | 'water' | 'growthIndex' | 'deadCount', boolean>> = {},
    // The plot area's height in pixels, used only to express the fixed-pixel context bands and gaps
    // above as the percentages Highcharts' axis `top`/`height` want. An estimate is fine and is what
    // callers pass: being a few pixels out moves a band by a fraction of its own gap.
    plotHeightPx: number = 600
): BandLayout {
    const asPct = (px: number) => (px / Math.max(plotHeightPx, 1)) * 100;
    const bandGapPct = asPct(BAND_GAP_PX);
    const bandVisible: Record<'weight' | 'water' | 'growthIndex' | 'deadCount', boolean> = {
        // weight and Daily Gain overlay the same box, so the band shows if either is toggled on.
        weight: hasWeightSensor && (toggles.weight || toggles.dailyGain),
        // water actual and Water Norm overlay the same box, so the band shows if either is toggled on.
        water: hasWaterSensor && (toggles.water || toggles.waterNorm),
        growthIndex: hasWeightSensor && toggles.growthIndex,
        deadCount: toggles.deadCount,
    };

    // Top-to-bottom order: weight/Daily Gain, then water, then the two secondary sub-bands. Each
    // secondary band is read by skimming it against the primary curve above it, so it has to sit
    // below the curve it is being compared with.
    const visibleBandIds = (['weight', 'water', 'growthIndex', 'deadCount'] as const).filter((id) => bandVisible[id]);

    // Whether the band at `index` opens a gap above itself. A fused band only skips its gap when the
    // band it fuses with is the one actually above it - hide weight and the water band is no longer
    // continuing anything, so it takes its gap back and reads as its own box again.
    const opensGapAbove = (index: number) => index > 0 && FUSED_WITH_BAND_ABOVE[visibleBandIds[index]] !== visibleBandIds[index - 1];

    const productionGroupHeight = hasProductionGroups ? asPct(PRODUCTION_GROUP_BAND_HEIGHT_PX) : 0;
    const feedMixHeight = hasFeedMixes ? asPct(FEED_MIX_BAND_HEIGHT_PX) : 0;
    const contextHeight = productionGroupHeight + feedMixHeight;
    const gapAfterContext = contextHeight > 0 && visibleBandIds.length > 0 ? bandGapPct : 0;
    const gapsBetweenDataBands = visibleBandIds.reduce((total, _id, index) => total + (opensGapAbove(index) ? bandGapPct : 0), 0);
    const availableHeight = 100 - contextHeight - gapAfterContext - gapsBetweenDataBands;
    const ratioOf = (id: 'weight' | 'water' | 'growthIndex' | 'deadCount') => (emptyBands[id] ? EMPTY_BAND_RATIO : BAND_HEIGHT_RATIO[id]);
    const totalRatio = visibleBandIds.reduce((sum, id) => sum + ratioOf(id), 0);

    const layout: BandLayout = {
        productionGroups: hasProductionGroups ? { top: 0, height: productionGroupHeight } : null,
        feedMixes: hasFeedMixes ? { top: productionGroupHeight, height: feedMixHeight } : null,
        weight: null,
        water: null,
        growthIndex: null,
        deadCount: null,
    };

    let cursor = contextHeight + gapAfterContext;
    visibleBandIds.forEach((id, index) => {
        if (opensGapAbove(index)) cursor += bandGapPct;
        const height = (ratioOf(id) / totalRatio) * availableHeight;
        layout[id] = { top: cursor, height };
        cursor += height;
    });

    return layout;
}
